import {
  Controller,
  FieldPath,
  FieldValues,
  UseFormReturn,
} from 'react-hook-form';
import { TextField, TextFieldProps } from '@mui/material';

type NumberInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = {
  form: UseFormReturn<TFieldValues>;
  name: TName;
} & Omit<
  TextFieldProps,
  'name' | 'value' | 'onBlur' | 'ref' | 'error' | 'helperText'
>;

export function NumberInput<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({ form, name, ...rest }: NumberInputProps<TFieldValues, TName>) {
  const { error } = form.getFieldState(name, form.formState);
  const value = form.watch(name);
  // prevents label from being hidden when the field is empty
  // see https://github.com/react-hook-form/react-hook-form/issues/220
  const shrink = value !== undefined && value !== null && value !== '';

  const parse = (value: string) => {
    const parsed = Number(value);
    if (isNaN(parsed)) {
      return undefined;
    }
    return parsed;
  };

  return (
    <Controller
      control={form.control}
      name={name}
      render={({ field }) => (
        <TextField
          {...rest}
          {...field}
          value={field.value?.toString()}
          onChange={(event) => {
            field.onChange(parse(event.target.value));
            rest.onChange?.(event);
          }}
          onBlur={field.onBlur}
          error={!!error}
          type="number"
          helperText={error?.message}
          slotProps={{
            inputLabel: { shrink },
            ...(rest.slotProps ?? {}),
          }}
        />
      )}
    />
  );
}
